import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
  seriesIndex?: any;
  dataPointIndex?: any;
  w: any;
  currentYear: string;
  selectedMonth?: string | null;
  isSRBM?: boolean;
}

const CustomYearTooltip: React.FC<Props> = ({ seriesIndex, dataPointIndex, w, currentYear, selectedMonth, isSRBM }) => {
  const { t } = useTranslation();
  const seriesData = w.config.series[seriesIndex]?.data[dataPointIndex] || {};
  const monthAbbr = w.config.series[seriesIndex].name;
  const isSRBMData = seriesData.times.some((signal: string) => signal.includes('srbm'));

  const yValue = monthAbbr;
  const xValue = seriesData.x;
  const date = dayjs(`${currentYear}-${selectedMonth}-${yValue}`).format('ddd');
  const month = dayjs(`${currentYear}-${selectedMonth}-${yValue}`).format('MMM');

  const captureCount = seriesData.captureCount ?? 0;
  const nonCaptureCount = seriesData.nonCaptureCount ?? 0;
  const eatenBaitAmount = seriesData.eaten_bait ?? 0;
  const isUpcoming = seriesData.y === -1000000;
  const hasNoActivity = captureCount === 0 && nonCaptureCount === 0;

  const captureSignalsMCT = seriesData.times.filter(
    (signal: string) => signal.includes('APPROVED_MICE') && signal.includes('mct'),
  );
  const captureSignalsSRST = seriesData.times.filter(
    (signal: string) => signal.includes('APPROVED_MICE') && signal.includes('srst'),
  );
  const captureSignalsSMST = seriesData.times.filter(
    (signal: string) => signal.includes('APPROVED_MICE') && signal.includes('smst'),
  );

  const nonCaptureSignalsMCT = seriesData.times.filter(
    (signal: string) => !signal.includes('APPROVED_MICE') && signal.includes('mct'),
  );
  const nonCaptureSignalsSMST = seriesData.times.filter(
    (signal: string) => !signal.includes('APPROVED_MICE') && signal.includes('smst'),
  );
  const nonCaptureSignalsSRST = seriesData.times.filter(
    (signal: string) => !signal.includes('APPROVED_MICE') && signal.includes('srst'),
  );
  const roundedCountNumbers = (count: number) => {
    let enterOrBait = '';
    if (count < 0.01) {
      enterOrBait = '0';
    } else {
      enterOrBait = count.toFixed(2);
    }
    return enterOrBait;
  };
  return (
    <div className="popover" style={{ width: '280px', maxWidth: '100%' }}>
      <div className="popover-header m-0 py-1 bg-light">
        <div className="d-flex align-items-center mx-2 justify-content-between gap-3">
          <div className="d-flex">
            {selectedMonth ? (
              <>
                {`${date} ${yValue}th `}
                <br />
                {`at ${xValue.split(':')[0]}:00 - ${xValue.split(':')[0]}:59`}
              </>
            ) : (
              <>{`${yValue} ${xValue}`}</>
            )}
          </div>

          {!isUpcoming && (
            <div className="d-flex justify-content-between flex-column" style={{ alignItems: 'flex-start' }}>
              {isSRBMData ? (
                <div>
                  <div className="m-0 small">
                    {t('MCTDetail.heatmap.titleSRBMWithOut')}: {captureCount}
                  </div>
                  <div className="m-0 small">
                    {t('MCTDetail.heatmap.titleSRBMWith')}: {nonCaptureCount}
                  </div>
                  <div className="m-0 small">
                    {t('MCTDetail.heatmap.titleEatinBait')}: {roundedCountNumbers(eatenBaitAmount)}g
                  </div>
                </div>
              ) : (
                <div>
                  <h6 className={`m-0 ${isSRBM ? 'small' : ''} `}>
                    {isSRBM ? t('MCTDetail.heatmap.titleSRBMWithOut') : t('MCTDetail.heatmap.captures')} :{' '}
                    {captureCount}
                  </h6>
                  <h6 className={`m-0 ${isSRBM ? 'small' : ''} `}>
                    {isSRBM ? t('MCTDetail.heatmap.titleSRBMWith') : t('MCTDetail.heatmap.no_captures')}:{' '}
                    {nonCaptureCount}
                  </h6>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={`${isSRBMData ? '' : 'popover-body'} text-center`}>
        {hasNoActivity ? (
          <h6>
            {t('MCTDetail.heatmap.no_activity')} {t('MCTDetail.heatmap.on')}{' '}
            {seriesData.x.includes(':') ? `${month} ${monthAbbr}` : seriesData.x}
            {t('MCTDetail.heatmap.th')}{' '}
            {seriesData.x.includes(':') ? t('MCTDetail.heatmap.at') : t('MCTDetail.heatmap.of')}{' '}
            {!seriesData.x.includes(':') ? monthAbbr : `${seriesData.x}-${seriesData.x.replace(':00', ':59')}`}
          </h6>
        ) : (
          <div
            className="d-flex flex-column px-3"
            style={{ alignItems: 'flex-start', textAlign: 'left', width: '100%' }}
          >
            {captureSignalsMCT.length > 0 && (
              <div>
                <h6 className="mb-1">
                  {t('ChartsAnalitics.heatmap_chart.mctCapture')}: {captureSignalsMCT.length}
                </h6>
              </div>
            )}
            {captureSignalsSRST.length > 0 && (
              <div>
                <h6 className="mb-1">
                  {t('ChartsAnalitics.heatmap_chart.srstCapture')}: {captureSignalsSRST.length}
                </h6>
              </div>
            )}
            {captureSignalsSMST.length > 0 && (
              <div>
                <h6 className="mb-1">
                  {t('ChartsAnalitics.heatmap_chart.smstCapture')}: {captureSignalsSMST.length}
                </h6>
              </div>
            )}

            {nonCaptureSignalsMCT.length > 0 && (
              <div>
                <h6 className="mb-1">
                  {t('ChartsAnalitics.heatmap_chart.mctNonCapture')}: {nonCaptureSignalsMCT.length}
                </h6>
              </div>
            )}
            {nonCaptureSignalsSMST.length > 0 && (
              <div>
                <h6 className="mb-1">
                  {t('ChartsAnalitics.heatmap_chart.smstNonCapture')}: {nonCaptureSignalsSMST.length}
                </h6>
              </div>
            )}
            {nonCaptureSignalsSRST.length > 0 && (
              <div>
                <h6 className="mb-1">
                  {t('ChartsAnalitics.heatmap_chart.srstNonCapture')}: {nonCaptureSignalsSRST.length}
                </h6>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomYearTooltip;
